/**
 * Anonymous discovery homepage category personalization (browser-only).
 *
 * ADMIN `homePriority` remains primary; click weights break ties within the
 * eligible tile set returned by `/public/discovery/home-categories`.
 */

import type { PublicDiscoveryHomeCategoryItemDTO } from './publicDiscoveryTypes.js';

const STORAGE_KEY = 'customer:discovery:homeCategoryPersonalization' as const;

/** Small increment per category open — enough to reorder ties, not override ADMIN. */
const WEIGHT_INCREMENT = 1;

type CategoryWeightMap = Record<string, number>;

function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null;
  }
  return window.localStorage;
}

function readWeights(): CategoryWeightMap {
  const storage = getLocalStorage();
  if (storage === null) {
    return {};
  }
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw === null || raw.trim().length === 0) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const weights: CategoryWeightMap = {};
    for (const [slug, value] of Object.entries(parsed)) {
      if (typeof slug !== 'string' || slug.trim().length === 0) {
        continue;
      }
      if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        continue;
      }
      weights[slug] = value;
    }
    return weights;
  } catch {
    return {};
  }
}

function writeWeights(weights: CategoryWeightMap): void {
  const storage = getLocalStorage();
  if (storage === null) {
    return;
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(weights));
  } catch {
    // Best-effort anonymous personalization — ignore quota / private mode.
  }
}

function normalizeSlug(slug: string): string | null {
  const trimmed = slug.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Record one category tile open for anonymous personalization. */
export function recordCategoryOpen(slug: string): void {
  const normalized = normalizeSlug(slug);
  if (normalized === null) {
    return;
  }
  const weights = readWeights();
  weights[normalized] = (weights[normalized] ?? 0) + WEIGHT_INCREMENT;
  writeWeights(weights);
}

function compareHomeCategoryItems(
  a: PublicDiscoveryHomeCategoryItemDTO,
  b: PublicDiscoveryHomeCategoryItemDTO,
  weights: CategoryWeightMap,
): number {
  const priorityDiff = a.homePriority - b.homePriority;
  if (priorityDiff !== 0) {
    return priorityDiff;
  }

  const weightDiff = (weights[b.slug] ?? 0) - (weights[a.slug] ?? 0);
  if (weightDiff !== 0) {
    return weightDiff;
  }

  const sortOrderDiff = a.sortOrder - b.sortOrder;
  if (sortOrderDiff !== 0) {
    return sortOrderDiff;
  }

  return a.id - b.id;
}

/**
 * Stable sort within the eligible homepage category set only.
 * ADMIN `homePriority` first; personalization weight breaks ties.
 */
export function applyPersonalizationSort(
  items: readonly PublicDiscoveryHomeCategoryItemDTO[],
): PublicDiscoveryHomeCategoryItemDTO[] {
  const weights = readWeights();
  return [...items].sort((a, b) => compareHomeCategoryItems(a, b, weights));
}

/** Sort eligible items, then cap to ADMIN slot policy (personalization before cap). */
export function selectVisibleHomeCategories(
  items: readonly PublicDiscoveryHomeCategoryItemDTO[],
  slotCount: 3 | 6,
): PublicDiscoveryHomeCategoryItemDTO[] {
  return applyPersonalizationSort(items).slice(0, slotCount);
}
